import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Plus, Upload, FileText, Send, DollarSign, 
  TrendingUp, Clock, CheckCircle, XCircle, Eye, X,
  Mail, Phone, MessageSquare, Calendar, ChevronRight, Edit2,
  Search, ChevronLeft, Unlock, AlertTriangle, Star
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import StarRating from '../components/StarRating';

// Simple in-memory cache
const pageCache = {
  data: null,
  timestamp: 0,
  key: ''
};
const CACHE_DURATION = 60000; // 1 minute

export default function ReferidosPage({ currentUser, isAdminView = false }) {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [commissionForm, setCommissionForm] = useState({ amount: 0, status: 'pending' });
  const loadingRef = useRef(false);
  
  // Tab state for admin view
  const [activeTab, setActiveTab] = useState('referidores'); // 'referidores', 'leads' or 'commissions'
  
  // Referidores state
  const [referidores, setReferidores] = useState([]);
  const [loadingReferidores, setLoadingReferidores] = useState(false);
  const [referidorSearch, setReferidorSearch] = useState('');
  const [services, setServices] = useState([]);
  
  // Pagination state - default to 10 items
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    description: '',
    documents: []
  });

  // Check if current user is admin - either by role or by isAdminView prop
  const isAdmin = currentUser?.role === 'admin' || isAdminView === true;

  // Calculate days since last toque for a referidor
  const getDaysSinceLastToque = (toques) => {
    if (!toques || toques.length === 0) return null;
    
    const sortedToques = [...toques].sort((a, b) => 
      new Date(b.createdAt) - new Date(a.createdAt)
    );
    const lastToque = sortedToques[0];
    if (!lastToque?.createdAt) return null;
    
    const lastDate = new Date(lastToque.createdAt);
    const today = new Date();
    const diffTime = Math.abs(today - lastDate);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Filter referidores by search term (name, tag, services, toques)
  const filteredReferidores = referidores.filter((ref) => {
    if (!referidorSearch.trim()) return true;
    
    const searchLower = referidorSearch.toLowerCase().trim();
    
    // Search in name
    if (ref.name?.toLowerCase().includes(searchLower)) return true;
    
    // Search in email
    if (ref.email?.toLowerCase().includes(searchLower)) return true;
    
    // Search in profile tag
    if (ref.profileTag?.toLowerCase().includes(searchLower)) return true;
    
    // Search in optimal services
    if (ref.optimalServices?.length > 0) {
      const serviceNames = ref.optimalServices.map(sId => {
        const service = services.find(s => s.id === sId);
        return service?.name?.toLowerCase() || '';
      });
      if (serviceNames.some(name => name.includes(searchLower))) return true;
    }
    
    // Search in toques comments
    if (ref.toques?.length > 0) {
      const toqueTexts = ref.toques.map(t => 
        `${t.comment || ''} ${t.customChannel || ''} ${t.channel || ''}`.toLowerCase()
      );
      if (toqueTexts.some(text => text.includes(searchLower))) return true;
    }
    
    return false;
  });

  const loadLeads = useCallback(async (forceRefresh = false) => {
    // Prevent duplicate calls
    if (loadingRef.current) return;
    
    const cacheKey = `referidos_${pagination.page}_${pagination.limit}_${searchTerm}`;
    
    // Check cache first (only for initial load or if not forcing refresh)
    if (!forceRefresh && pageCache.key === cacheKey && Date.now() - pageCache.timestamp < CACHE_DURATION) {
      setLeads(pageCache.data.leads || []);
      setStats(pageCache.data.stats || {});
      setPagination(prev => ({
        ...prev,
        total: pageCache.data.pagination?.total || 0,
        totalPages: pageCache.data.pagination?.totalPages || 0
      }));
      setLoading(false);
      setInitialLoad(false);
      return;
    }
    
    loadingRef.current = true;
    // Only show loading spinner on initial load, not on pagination
    if (initialLoad) {
      setLoading(true);
    }
    
    try {
      if (isAdmin) {
        // Admin uses paginated endpoint
        const response = await api.getAllReferidoLeadsPaginated(
          pagination.page, 
          pagination.limit, 
          searchTerm
        );
        
        // Update cache
        pageCache.data = response;
        pageCache.timestamp = Date.now();
        pageCache.key = cacheKey;
        
        setLeads(response.leads || []);
        setStats(response.stats || {});
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0,
          totalPages: response.pagination?.totalPages || 0
        }));
      } else {
        // Referidores use their own endpoint (not paginated for now)
        const response = await api.getMyReferidoLeads();
        setLeads(response.leads || []);
        setStats(response.stats || {});
      }
    } catch (error) {
      console.error('Error loading leads:', error);
      toast.error('Error al cargar leads');
    } finally {
      setLoading(false);
      setInitialLoad(false);
      loadingRef.current = false;
    }
  }, [isAdmin, pagination.page, pagination.limit, searchTerm, initialLoad]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== searchTerm) {
        setSearchTerm(searchInput);
        setPagination(prev => ({ ...prev, page: 1 }));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput, searchTerm]);

  // Load referidores list
  const loadReferidores = useCallback(async () => {
    if (!isAdmin) return;
    setLoadingReferidores(true);
    try {
      const [data, servicesData] = await Promise.all([
        api.getReferidores(),
        api.getServices()
      ]);
      setReferidores(data);
      setServices(servicesData || []);
    } catch (error) {
      console.error('Error loading referidores:', error);
    } finally {
      setLoadingReferidores(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (activeTab === 'referidores' && isAdmin) {
      loadReferidores();
    }
  }, [activeTab, isAdmin, loadReferidores]);

  const handleReferidorRating = async (referidorId, rating) => {
    try {
      await api.updateReferidorRating(referidorId, rating);
      setReferidores(prev => prev.map(r => r.id === referidorId ? { ...r, referidorRating: rating } : r));
      toast.success('Calificación actualizada');
    } catch (error) {
      toast.error('Error al actualizar calificación');
    }
  };

  // Handle rating update
  const handleRatingUpdate = async (leadId, rating) => {
    try {
      await api.updateLeadRating(leadId, rating);
      // Update local state
      setLeads(prev => prev.map(lead => 
        lead.id === leadId ? { ...lead, rating } : lead
      ));
    } catch (error) {
      console.error('Error updating rating:', error);
      toast.error('Error al actualizar calificación');
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    files.forEach(file => {
      if (file.size > maxSize) {
        toast.error(`${file.name} es demasiado grande. Máximo 10MB`);
        return;
      }
      
      const reader = new FileReader();
      reader.onload = () => {
        setFormData(prev => ({
          ...prev,
          documents: [...prev.documents, {
            name: file.name,
            fileData: reader.result.split(',')[1],
            fileType: file.type,
            size: file.size
          }]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeDocument = (index) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) {
      toast.error('Nombre, correo y teléfono son requeridos');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.createReferidoLead(formData);
      toast.success('¡Lead referido enviado exitosamente!');
      setShowNewModal(false);
      setFormData({ name: '', email: '', phone: '', description: '', documents: [] });
      loadLeads();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Error al enviar lead');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateCommission = async () => {
    if (!selectedLead) return;
    
    try {
      await api.updateLeadCommission(selectedLead.id, commissionForm.amount, commissionForm.status);
      toast.success('Comisión actualizada');
      setShowCommissionModal(false);
      setSelectedLead(null);
      loadLeads(true); // Force refresh to get updated data
    } catch (error) {
      toast.error('Error al actualizar comisión');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-700';
      case 'approved': return 'bg-blue-100 text-blue-700';
      case 'paid': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Pendiente';
      case 'approved': return 'Aprobada';
      case 'paid': return 'Pagada';
      case 'rejected': return 'Rechazada';
      default: return 'Sin estado';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isAdmin ? 'Gestión de Referidos' : 'Mis Leads Referidos'}
          </h1>
          <p className="text-slate-500 mt-1">
            {isAdmin ? 'Administra todos los leads referidos y comisiones' : 'Envía y gestiona tus leads referidos'}
          </p>
        </div>
        {!isAdmin && (
          <Button onClick={() => setShowNewModal(true)} className="gap-2">
            <Plus className="w-4 h-4" />
            Nuevo Lead
          </Button>
        )}
      </div>

      {/* Admin Tabs */}
      {isAdmin && (
        <div className="border-b border-slate-200">
          <nav className="flex gap-4">
            <button
              onClick={() => setActiveTab('referidores')}
              className={`pb-3 px-1 font-medium text-sm transition-colors border-b-2 ${
                activeTab === 'referidores'
                  ? 'text-indigo-600 border-indigo-600'
                  : 'text-slate-500 border-transparent hover:text-slate-700'
              }`}
              data-testid="tab-referidores"
            >
              <Star className="w-4 h-4 inline mr-2" />
              Referidores
            </button>
            <button
              onClick={() => { setActiveTab('leads'); loadLeads(true); }}
              className={`pb-3 px-1 font-medium text-sm transition-colors border-b-2 ${
                activeTab === 'leads'
                  ? 'text-indigo-600 border-indigo-600'
                  : 'text-slate-500 border-transparent hover:text-slate-700'
              }`}
              data-testid="tab-leads-referidos"
            >
              <Users className="w-4 h-4 inline mr-2" />
              Leads Referidos
            </button>
            <button
              onClick={() => setActiveTab('commissions')}
              className={`pb-3 px-1 font-medium text-sm transition-colors border-b-2 ${
                activeTab === 'commissions'
                  ? 'text-indigo-600 border-indigo-600'
                  : 'text-slate-500 border-transparent hover:text-slate-700'
              }`}
              data-testid="tab-comisiones"
            >
              <DollarSign className="w-4 h-4 inline mr-2" />
              Comisiones
            </button>
          </nav>
        </div>
      )}

      {/* ===== REFERIDORES TAB ===== */}
      {isAdmin && activeTab === 'referidores' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="referidores-list">
          {/* Search Bar */}
          <div className="p-4 border-b border-slate-200 bg-slate-50">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Buscar por nombre, tag, servicio o comentario de toque..."
                value={referidorSearch}
                onChange={(e) => setReferidorSearch(e.target.value)}
                className="pl-10 bg-white"
                data-testid="referidor-search"
              />
            </div>
            {referidorSearch && (
              <p className="text-xs text-slate-500 mt-2">
                Mostrando {filteredReferidores.length} de {referidores.length} referidores
              </p>
            )}
          </div>
          
          {loadingReferidores ? (
            <div className="p-12 flex justify-center">
              <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : filteredReferidores.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              {referidorSearch ? (
                <>
                  <p className="text-slate-500 text-lg font-medium">No se encontraron referidores</p>
                  <p className="text-slate-400 text-sm mt-1">Intenta con otros términos de búsqueda</p>
                  <Button variant="outline" onClick={() => setReferidorSearch('')} className="mt-4">
                    Limpiar búsqueda
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-slate-500 text-lg font-medium">No hay referidores registrados</p>
                  <p className="text-slate-400 text-sm mt-1">Los referidores se crean desde la sección de Usuarios con el rol "Referidor"</p>
                </>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Referidor</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Contacto</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Calidad</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Último Toque</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Leads</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Comisión Total</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Pagada</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Pendiente</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredReferidores.map((ref) => (
                    <tr key={ref.id} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => navigate(`/referidos/${ref.id}`)}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-bold text-indigo-600">{ref.name?.charAt(0)?.toUpperCase() || '?'}</span>
                          </div>
                          <p className="font-medium text-slate-900">{ref.name}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-0.5">
                          <p className="text-sm text-slate-600 flex items-center gap-1"><Mail className="w-3 h-3 text-slate-400" /> {ref.email}</p>
                          {ref.phone && <p className="text-sm text-slate-600 flex items-center gap-1"><Phone className="w-3 h-3 text-slate-400" /> {ref.phone}</p>}
                        </div>
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-center">
                          <StarRating rating={ref.referidorRating || 0} onRate={(r) => handleReferidorRating(ref.id, r)} size="sm" />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {(() => {
                          const days = getDaysSinceLastToque(ref.toques);
                          if (days === null) {
                            return <span className="text-xs text-slate-400">Sin toques</span>;
                          }
                          return (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                              days > 30 ? 'bg-red-100 text-red-700' :
                              days > 14 ? 'bg-amber-100 text-amber-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              <Clock className="w-3 h-3" />
                              {days === 0 ? 'Hoy' : days === 1 ? '1 día' : `${days} días`}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                          {ref.totalLeads || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-slate-900">
                        {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(ref.totalCommission || 0)}
                      </td>
                      <td className="px-4 py-3 text-right text-green-600 font-medium">
                        {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(ref.paidCommission || 0)}
                      </td>
                      <td className="px-4 py-3 text-right text-amber-600 font-medium">
                        {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(ref.pendingCommission || 0)}
                      </td>
                      <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => navigate(`/referidos/${ref.id}`)}
                          className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg"
                          title="Ver detalle"
                          data-testid={`view-referidor-${ref.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Stats Cards - Show only on leads tab */}
      {activeTab === 'leads' && (
      <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Leads</p>
              <p className="text-2xl font-bold text-slate-900">{stats.totalLeads || pagination.total || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Comisiones Pagadas</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.totalCommissionPaid)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Comisiones Pendientes</p>
              <p className="text-2xl font-bold text-amber-600">{formatCurrency(stats.pendingCommission)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search (Admin only) */}
      {isAdmin && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Buscar por nombre, email, teléfono o referidor..."
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      )}

      {/* Leads Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-slate-500">Cargando leads...</p>
          </div>
        ) : leads.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">
              {searchTerm ? 'No se encontraron leads' : 'No hay leads referidos'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Lead</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Contacto</th>
                    {isAdmin && (
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Referidor</th>
                    )}
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Embudo</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Calificación</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Comisión</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Estado</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Fecha</th>
                    {isAdmin && (
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Acciones</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {leads.map((lead) => (
                    <tr key={lead.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-slate-900">{lead.name}</p>
                          <p className="text-xs text-slate-500 truncate max-w-[150px]">{lead.description || '-'}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          <p className="text-sm text-slate-700 flex items-center gap-1">
                            <Mail className="w-3 h-3 text-slate-400" />
                            {lead.email}
                          </p>
                          <p className="text-sm text-slate-700 flex items-center gap-1">
                            <Phone className="w-3 h-3 text-slate-400" />
                            {lead.phone}
                          </p>
                        </div>
                      </td>
                      {isAdmin && (
                        <td className="px-4 py-3">
                          <p className="text-sm text-slate-700">{lead.referidorName || '-'}</p>
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span 
                            className="w-2 h-2 rounded-full" 
                            style={{ backgroundColor: lead.stageColor || '#6366f1' }}
                          ></span>
                          <div>
                            <p className="text-sm text-slate-700">{lead.pipelineName || '-'}</p>
                            <p className="text-xs text-slate-500">{lead.stageName || '-'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <StarRating 
                          rating={lead.rating || 0} 
                          onRate={(rating) => handleRatingUpdate(lead.id, rating)}
                          size="sm"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-slate-900">
                          {formatCurrency(lead.commissionAmount)}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(lead.commissionStatus)}`}>
                          {getStatusLabel(lead.commissionStatus)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-slate-500">{formatDate(lead.createdAt)}</p>
                      </td>
                      {isAdmin && (
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => navigate(`/lead/${lead.id}`)}
                              className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Ver lead"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedLead(lead);
                                setCommissionForm({
                                  amount: lead.commissionAmount || 0,
                                  status: lead.commissionStatus || 'pending'
                                });
                                setShowCommissionModal(true);
                              }}
                              className="p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Editar comisión"
                            >
                              <DollarSign className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination (Admin only) */}
            {isAdmin && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
                <p className="text-sm text-slate-500">
                  Mostrando {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                    disabled={pagination.page <= 1}
                    className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-slate-700">
                    Página {pagination.page} de {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                    disabled={pagination.page >= pagination.totalPages}
                    className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      </>
      )}

      {/* Commissions Tab - Admin Only */}
      {isAdmin && activeTab === 'commissions' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="p-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900">Todas las Comisiones</h2>
            <p className="text-sm text-slate-500 mt-1">Gestiona las comisiones de todos los referidores</p>
          </div>
          
          {/* Commission Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-4 bg-slate-50 border-b border-slate-200">
            <div className="text-center">
              <p className="text-2xl font-bold text-slate-900">
                {formatCurrency(leads.reduce((sum, l) => sum + (l.commissionAmount || 0), 0))}
              </p>
              <p className="text-xs text-slate-500">Total</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-600">
                {formatCurrency(leads.filter(l => l.commissionStatus === 'pending').reduce((sum, l) => sum + (l.commissionAmount || 0), 0))}
              </p>
              <p className="text-xs text-slate-500">Pendientes</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(leads.filter(l => l.commissionStatus === 'approved').reduce((sum, l) => sum + (l.commissionAmount || 0), 0))}
              </p>
              <p className="text-xs text-slate-500">Aprobadas</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-600">
                {formatCurrency(leads.filter(l => l.commissionStatus === 'paid').reduce((sum, l) => sum + (l.commissionAmount || 0), 0))}
              </p>
              <p className="text-xs text-slate-500">Pagadas</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">
                {formatCurrency(leads.filter(l => l.commissionStatus === 'rejected').reduce((sum, l) => sum + (l.commissionAmount || 0), 0))}
              </p>
              <p className="text-xs text-slate-500">Rechazadas</p>
            </div>
          </div>
          
          {/* Commissions Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Referidor</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Lead</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Empresa</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Monto</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Estado</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {leads.filter(l => l.commissionAmount > 0).length > 0 ? (
                  leads.filter(l => l.commissionAmount > 0).map((lead) => (
                    <tr key={lead.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <p className="font-medium text-slate-900">{lead.referrerName || 'Sin referidor'}</p>
                        <p className="text-xs text-slate-500">{lead.referrerEmail || ''}</p>
                      </td>
                      <td className="py-3 px-4">
                        <p className="font-medium text-slate-900">{lead.name}</p>
                        <p className="text-xs text-slate-500">{lead.email}</p>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">{lead.company || '-'}</td>
                      <td className="py-3 px-4 text-right font-semibold text-slate-900">
                        {formatCurrency(lead.commissionAmount)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                          lead.commissionStatus === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                          lead.commissionStatus === 'approved' ? 'bg-blue-100 text-blue-700' :
                          lead.commissionStatus === 'pending' ? 'bg-amber-100 text-amber-700' :
                          lead.commissionStatus === 'rejected' ? 'bg-red-100 text-red-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {getStatusLabel(lead.commissionStatus)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() => {
                            setSelectedLead(lead);
                            setCommissionForm({ 
                              amount: lead.commissionAmount || 0, 
                              status: lead.commissionStatus || 'pending' 
                            });
                            setShowCommissionModal(true);
                          }}
                          className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg"
                          title="Editar comisión"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="py-12 text-center">
                      <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No hay comisiones registradas</p>
                      <p className="text-sm text-slate-400 mt-1">Las comisiones aparecerán aquí cuando se asignen a los leads</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* New Lead Modal */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-lg font-semibold text-slate-900">Nuevo Lead Referido</h3>
              <button onClick={() => setShowNewModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nombre *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nombre completo"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Correo *</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="correo@ejemplo.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Teléfono *</label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+57 300 123 4567"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Descripción</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="¿Cómo conociste a este lead?"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Documentos</label>
                <input
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload"
                />
                <label 
                  htmlFor="file-upload"
                  className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50"
                >
                  <Upload className="w-5 h-5 text-slate-400" />
                  <span className="text-slate-500">Adjuntar archivos (máx 10MB)</span>
                </label>
                {formData.documents.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {formData.documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-slate-100 rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-slate-500" />
                          <span className="text-sm text-slate-700">{doc.name}</span>
                        </div>
                        <button type="button" onClick={() => removeDocument(idx)} className="text-red-500 hover:text-red-700">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowNewModal(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button type="submit" disabled={submitting} className="flex-1 gap-2">
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Enviar Lead
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Commission Modal */}
      {showCommissionModal && selectedLead && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-slate-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-slate-900">Editar Comisión</h3>
              <button onClick={() => setShowCommissionModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Lead</p>
                <p className="font-medium text-slate-900">{selectedLead.name}</p>
                <p className="text-sm text-slate-500">{selectedLead.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Monto de Comisión (COP)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                  <Input
                    type="text"
                    value={commissionForm.amount ? commissionForm.amount.toLocaleString('es-CO') : ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      setCommissionForm({ ...commissionForm, amount: parseInt(value) || 0 });
                    }}
                    className="pl-7"
                    placeholder="0"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Estado</label>
                <select
                  value={commissionForm.status}
                  onChange={(e) => setCommissionForm({ ...commissionForm, status: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="pending">Pendiente</option>
                  <option value="approved">Aprobada</option>
                  <option value="paid">Pagada</option>
                  <option value="rejected">Rechazada</option>
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowCommissionModal(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button onClick={handleUpdateCommission} className="flex-1 gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Guardar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
